/**
 * MobileMyShiftsList — Care Worker "My Shifts" mobile / tablet view
 * (<1024 px). Read-only in Batch 4.2: shift cards are tap-to-view, no
 * Accept / Decline / Check-in / Check-out surface is rendered here.
 *
 * Non-negotiables:
 *   • DESKTOP (≥1024 px) IS NOT TOUCHED — this component only renders
 *     when the parent wraps it in `lg:hidden`.
 *   • No mutation calls. The existing `POST /visits/{id}/accept|decline
 *     |check-in|check-out` endpoints are not invoked from mobile.
 *   • All fields shown come from the existing `/rota/my-shifts` +
 *     `/dashboard/client-safety` + `/sites` payloads — no fabricated
 *     data, no new API.
 *   • Permissions, tenancy / site filter, rota logic, attendance logic
 *     and payroll calculations are untouched.
 */
import React, { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import {
  Building2, Clock, AlertTriangle, ChevronRight, CalendarCheck, Sunrise,
  Sun, Sunset, Moon, Check, X, LogIn, LogOut, Activity,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "@/components/ui/dialog";
import CareFlagsPills from "@/components/CareFlagsPills";
import { asArray, ALLERGY_SEVERITY_STYLES, getAllergySeverity } from "@/lib/clientFields";

// -----------------------------------------------------------------------------
// Local helpers — presentation only. Do not add business logic here.
// -----------------------------------------------------------------------------
function pad2(n) { return String(n).padStart(2, "0"); }
function toLocalYMD(d) {
  return `${d.getFullYear()}-${pad2(d.getMonth() + 1)}-${pad2(d.getDate())}`;
}
function timeHM(iso) {
  if (!iso) return "";
  return new Date(iso).toLocaleTimeString(undefined, { hour: "2-digit", minute: "2-digit" });
}
function dayHeaderLabel(dayIso) {
  const now = new Date();
  const today = toLocalYMD(now);
  const tomorrow = new Date(now); tomorrow.setDate(tomorrow.getDate() + 1);
  const tomorrowIso = toLocalYMD(tomorrow);
  const d = new Date(dayIso + "T00:00:00");
  if (dayIso === today) return `Today · ${d.toLocaleDateString(undefined, { weekday: "short", day: "2-digit", month: "short" })}`;
  if (dayIso === tomorrowIso) return `Tomorrow · ${d.toLocaleDateString(undefined, { weekday: "short", day: "2-digit", month: "short" })}`;
  return d.toLocaleDateString(undefined, { weekday: "long", day: "2-digit", month: "short" });
}
function fullDateLabel(iso) {
  if (!iso) return "";
  const d = new Date(iso.slice(0, 10) + "T00:00:00");
  return d.toLocaleDateString(undefined, { weekday: "long", day: "2-digit", month: "long", year: "numeric" });
}
function shiftBlock(iso) {
  const h = new Date(iso).getHours();
  if (h >= 6 && h < 12) return { key: "morning", label: "Morning", Icon: Sunrise };
  if (h >= 12 && h < 17) return { key: "afternoon", label: "Afternoon", Icon: Sun };
  if (h >= 17 && h < 22) return { key: "evening", label: "Evening", Icon: Sunset };
  return { key: "night", label: "Night", Icon: Moon };
}

// Classify a visit into a single mobile lifecycle state. Presentation only —
// the underlying attendance / payroll fields (`check_in_at`, `check_out_at`,
// `acceptance_status`, `status`) are never modified here; we just read them.
function lifecycleOf(v) {
  if (v.status === "completed" || v.check_out_at) return "completed";
  if (v.check_in_at) return "on_shift";
  if (v.acceptance_status === "declined") return "declined";
  if (v.acceptance_status === "accepted") return "accepted";
  if (v.published_at) return "awaiting";
  return "scheduled";
}

// Format a stored ISO timestamp for display. Never fabricates a fresh clock
// value — always renders the value the backend returned.
function timeHMFromServer(iso) {
  if (!iso) return "";
  return new Date(iso).toLocaleTimeString(undefined, { hour: "2-digit", minute: "2-digit" });
}

// Worked duration derived from the two stored server timestamps. Never
// modifies or re-computes payable hours / worked hours — this is a purely
// visual "check_out_at − check_in_at" difference shown to the care worker.
function workedDurationLabel(inIso, outIso) {
  if (!inIso || !outIso) return null;
  const mins = Math.max(0, Math.round((new Date(outIso) - new Date(inIso)) / 60000));
  const h = Math.floor(mins / 60);
  const m = mins % 60;
  if (h === 0) return `${m}m`;
  if (m === 0) return `${h}h`;
  return `${h}h ${m}m`;
}

// -----------------------------------------------------------------------------
// Status badge (Accepted / Awaiting / Declined / Scheduled) — mirrors the
// existing desktop badge tones exactly. Read-only chip.
// -----------------------------------------------------------------------------
function StatusChip({ visit }) {
  const state = lifecycleOf(visit);
  const map = {
    completed: { label: "Completed", cls: "bg-emerald-50 border-emerald-300 text-emerald-800 dark:bg-emerald-950/30 dark:border-emerald-700 dark:text-emerald-300" },
    on_shift:  { label: "On shift",  cls: "bg-sky-50 border-sky-300 text-sky-800 dark:bg-sky-950/30 dark:border-sky-700 dark:text-sky-300" },
    accepted:  { label: "Accepted",  cls: "bg-emerald-50 border-emerald-300 text-emerald-800 dark:bg-emerald-950/30 dark:border-emerald-700 dark:text-emerald-300" },
    awaiting:  { label: "Awaiting accept", cls: "bg-amber-50 border-amber-300 text-amber-800 dark:bg-amber-950/30 dark:border-amber-700 dark:text-amber-300" },
    declined:  { label: "Declined",  cls: "bg-red-50 border-red-300 text-red-800 dark:bg-red-950/30 dark:border-red-700 dark:text-red-300" },
    scheduled: { label: "Scheduled", cls: "bg-slate-100 border-slate-300 text-slate-700 dark:bg-slate-800 dark:border-slate-600 dark:text-slate-200" },
  };
  const cfg = map[state] || map.scheduled;
  return (
    <span
      data-testid={`mobile-ms-status-${visit.id}`}
      data-lifecycle={state}
      className={`inline-flex items-center text-[10px] uppercase tracking-widest font-semibold px-2 py-0.5 rounded-md border ${cfg.cls}`}
    >
      {cfg.label}
    </span>
  );
}

// -----------------------------------------------------------------------------
// Shift card — whole card is a button that opens the read-only dialog.
// Priority order (top to bottom): Time → Service User → Site/House → Status.
// Purpose appears below when available. Critical allergy strip + care flags
// are shown only when the existing safety payload supplies them.
// -----------------------------------------------------------------------------
function ShiftCard({ visit, siteName, safety, onOpen }) {
  const block = shiftBlock(visit.scheduled_start);
  const BlockIcon = block.Icon;
  const critical = safety?.critical_allergies || [];
  const flags = safety?.care_flags || [];

  return (
    <button
      type="button"
      onClick={() => onOpen(visit)}
      data-testid={`mobile-ms-card-${visit.id}`}
      className="w-full text-left rounded-lg border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 p-3 min-h-[44px] active:bg-slate-50 dark:active:bg-slate-800 transition-colors focus:outline-none focus:ring-2 focus:ring-emerald-500"
    >
      <div className="flex items-start gap-3">
        {/* Time band */}
        <div className="w-16 shrink-0 text-center">
          <div className="text-[9px] uppercase tracking-widest text-slate-500 font-semibold inline-flex items-center gap-1 justify-center">
            <BlockIcon className="w-3 h-3" /> {block.label}
          </div>
          <div className="text-sm font-heading font-bold tabular-nums text-slate-900 dark:text-slate-100 mt-0.5 leading-tight">
            {timeHM(visit.scheduled_start)}
          </div>
          <div className="text-[10px] tabular-nums text-slate-500 dark:text-slate-400 leading-tight">
            {visit.scheduled_end ? `–${timeHM(visit.scheduled_end)}` : "…"}
          </div>
        </div>

        {/* Middle column: Service user, Site/House, Purpose */}
        <div className="flex-1 min-w-0">
          <div className="font-semibold text-slate-900 dark:text-slate-100 truncate" data-testid={`mobile-ms-client-${visit.id}`}>
            {visit.client_name || visit.client_id || "Service user"}
          </div>
          {siteName && (
            <div className="text-xs text-slate-600 dark:text-slate-400 mt-0.5 inline-flex items-center gap-1 max-w-full">
              <Building2 className="w-3 h-3 shrink-0" />
              <span className="truncate" data-testid={`mobile-ms-site-${visit.id}`}>{siteName}</span>
            </div>
          )}
          {visit.purpose && (
            <div className="text-xs text-slate-500 dark:text-slate-400 mt-0.5 line-clamp-2">
              {visit.purpose}
            </div>
          )}

          {/* Safety chips — only when the existing safety endpoint returned
              non-empty data for this client. Text + icon + colour. */}
          {critical.length > 0 && (
            <div
              className="mt-2 text-[11px] text-red-800 dark:text-red-300 bg-red-50 dark:bg-red-950/30 border-l-2 border-red-500 px-2 py-1 rounded-sm inline-flex items-start gap-1"
              data-testid={`mobile-ms-critical-${visit.id}`}
            >
              <AlertTriangle className="w-3 h-3 mt-0.5 shrink-0" />
              <span className="truncate">
                <span className="font-bold uppercase tracking-widest text-[10px]">{critical[0].severity}</span>{" "}
                {critical[0].item}
                {critical.length > 1 && (
                  <span className="opacity-70"> · +{critical.length - 1}</span>
                )}
              </span>
            </div>
          )}
          {flags.length > 0 && (
            <div className="mt-1.5"><CareFlagsPills flags={flags} size="sm" /></div>
          )}
        </div>

        {/* Right column: Status + chevron */}
        <div className="flex flex-col items-end gap-1 shrink-0">
          <StatusChip visit={visit} />
          <ChevronRight className="w-4 h-4 text-slate-400" aria-hidden />
        </div>
      </div>
    </button>
  );
}

// -----------------------------------------------------------------------------
// Shift Details dialog. Batch 4.2 added the read-only body; Batch 4.3 adds
// the care-worker lifecycle action footer (Accept / Decline / Check in /
// Check out). All actions call the parent-supplied handlers `onAct` /
// `onCheck`, which are the SAME handlers desktop uses — same endpoints,
// same request bodies, same server-side attendance writes. No new API,
// no client-side timestamp generation.
// -----------------------------------------------------------------------------
function ShiftDetailsDialog({
  visit, siteName, safety, onClose, canAct, busyId, onAct, onCheck,
}) {
  const critical = safety?.critical_allergies || [];
  const flags = safety?.care_flags || [];
  const block = shiftBlock(visit.scheduled_start);
  const allergies = asArray(visit.client_allergies);
  const state = lifecycleOf(visit);
  const busy = busyId === visit.id;
  const worked = workedDurationLabel(visit.check_in_at, visit.check_out_at);

  const handleAccept = () => { if (!busy && canAct) onAct(visit.id, "accept"); };
  // Decline confirmation is already inside the parent `act` handler
  // (window.confirm). Re-uses the desktop behaviour verbatim.
  const handleDecline = () => { if (!busy && canAct) onAct(visit.id, "decline"); };
  const handleCheckIn = () => { if (!busy && canAct) onCheck(visit.id, "check-in"); };
  const handleCheckOut = () => {
    if (busy || !canAct) return;
    // Batch 4.3 · mobile-only Check out confirmation to reduce accidental
    // taps. Desktop attendance behaviour is not changed.
    // eslint-disable-next-line no-alert
    if (!window.confirm("Check out of this shift? Your worked time will be recorded.")) return;
    onCheck(visit.id, "check-out");
  };

  return (
    <Dialog open onOpenChange={(v) => { if (!v) onClose(); }}>
      <DialogContent data-testid="mobile-ms-view-dialog" data-lifecycle={state}>
        <DialogHeader>
          <DialogTitle>Shift details</DialogTitle>
          <DialogDescription>{fullDateLabel(visit.scheduled_start)}</DialogDescription>
        </DialogHeader>

        <div className="space-y-3 text-sm">
          {/* Lifecycle banner — makes the current state unmistakable. */}
          {state === "on_shift" && (
            <div className="rounded-md border-l-4 border-sky-500 bg-sky-50 dark:bg-sky-950/40 px-3 py-2 flex items-center gap-2" data-testid="mobile-ms-on-shift-banner">
              <Activity className="w-4 h-4 text-sky-700 dark:text-sky-300 shrink-0 animate-pulse" />
              <div className="text-xs">
                <div className="text-[10px] uppercase tracking-widest font-bold text-sky-800 dark:text-sky-300">On shift</div>
                <div className="text-sky-900 dark:text-sky-200 tabular-nums">
                  Checked in at <span className="font-semibold" data-testid="mobile-ms-view-checkin-time">{timeHMFromServer(visit.check_in_at)}</span>
                </div>
              </div>
            </div>
          )}
          {state === "completed" && (
            <div className="rounded-md border-l-4 border-emerald-500 bg-emerald-50 dark:bg-emerald-950/40 px-3 py-2" data-testid="mobile-ms-completed-banner">
              <div className="text-[10px] uppercase tracking-widest font-bold text-emerald-800 dark:text-emerald-300 inline-flex items-center gap-1">
                <Check className="w-3.5 h-3.5" /> Completed
              </div>
              <div className="mt-1 grid grid-cols-3 gap-2 text-[11px] text-emerald-900 dark:text-emerald-200 tabular-nums">
                <div>
                  <div className="opacity-70 text-[9px] uppercase tracking-widest">In</div>
                  <div className="font-semibold" data-testid="mobile-ms-view-checkin-time">{timeHMFromServer(visit.check_in_at) || "—"}</div>
                </div>
                <div>
                  <div className="opacity-70 text-[9px] uppercase tracking-widest">Out</div>
                  <div className="font-semibold" data-testid="mobile-ms-view-checkout-time">{timeHMFromServer(visit.check_out_at) || "—"}</div>
                </div>
                <div>
                  <div className="opacity-70 text-[9px] uppercase tracking-widest">Worked</div>
                  <div className="font-semibold" data-testid="mobile-ms-view-worked">{worked || "—"}</div>
                </div>
              </div>
            </div>
          )}
          {state === "declined" && (
            <div className="rounded-md border-l-4 border-red-500 bg-red-50 dark:bg-red-950/40 px-3 py-2 flex items-center gap-2" data-testid="mobile-ms-declined-banner">
              <X className="w-4 h-4 text-red-700 dark:text-red-300 shrink-0" />
              <div className="text-xs">
                <div className="text-[10px] uppercase tracking-widest font-bold text-red-800 dark:text-red-300">Declined</div>
                <div className="text-red-900 dark:text-red-200">Your manager has been notified.</div>
              </div>
            </div>
          )}

          <div className="grid grid-cols-2 gap-2">
            <div className="rounded-md border border-slate-200 dark:border-slate-700 p-2">
              <div className="text-[9px] uppercase tracking-widest text-slate-500 font-semibold">Time</div>
              <div className="tabular-nums font-semibold text-slate-900 dark:text-slate-100 mt-0.5" data-testid="mobile-ms-view-time">
                {timeHM(visit.scheduled_start)}{visit.scheduled_end ? `–${timeHM(visit.scheduled_end)}` : ""}
              </div>
            </div>
            <div className="rounded-md border border-slate-200 dark:border-slate-700 p-2">
              <div className="text-[9px] uppercase tracking-widest text-slate-500 font-semibold">Block</div>
              <div className="font-semibold text-slate-900 dark:text-slate-100 mt-0.5">{block.label}</div>
            </div>
          </div>

          <div className="rounded-md border border-slate-200 dark:border-slate-700 p-2">
            <div className="text-[9px] uppercase tracking-widest text-slate-500 font-semibold">Service user</div>
            <div className="font-medium text-slate-900 dark:text-slate-100 mt-0.5 truncate" data-testid="mobile-ms-view-client">
              {visit.client_name || visit.client_id || "—"}
            </div>
          </div>

          {siteName && (
            <div className="rounded-md border border-slate-200 dark:border-slate-700 p-2">
              <div className="text-[9px] uppercase tracking-widest text-slate-500 font-semibold">Site / House</div>
              <div className="font-medium text-slate-900 dark:text-slate-100 mt-0.5 inline-flex items-center gap-1 truncate" data-testid="mobile-ms-view-site">
                <Building2 className="w-3.5 h-3.5" /> {siteName}
              </div>
            </div>
          )}

          {visit.purpose && (
            <div className="rounded-md border border-slate-200 dark:border-slate-700 p-2">
              <div className="text-[9px] uppercase tracking-widest text-slate-500 font-semibold">Purpose</div>
              <div className="text-sm text-slate-700 dark:text-slate-300 mt-0.5 whitespace-pre-wrap" data-testid="mobile-ms-view-purpose">
                {visit.purpose}
              </div>
            </div>
          )}

          <div className="flex flex-wrap gap-1.5" data-testid="mobile-ms-view-status">
            <StatusChip visit={visit} />
            {visit.status === "missed" && (
              <span className="text-[10px] uppercase tracking-widest font-semibold px-2 py-0.5 rounded-md border bg-red-50 border-red-300 text-red-800">
                Missed
              </span>
            )}
          </div>

          {/* Safety */}
          {critical.length > 0 && (
            <div className="rounded-md border-l-2 border-red-500 bg-red-50 dark:bg-red-950/30 px-3 py-2" data-testid="mobile-ms-view-critical">
              <div className="text-[10px] uppercase tracking-widest text-red-800 dark:text-red-300 font-bold inline-flex items-center gap-1">
                <AlertTriangle className="w-3.5 h-3.5" /> Critical allergy alert
              </div>
              <ul className="mt-1 text-[12px] text-red-900 dark:text-red-200 space-y-0.5">
                {critical.map((a, i) => (
                  <li key={i}>
                    <span className="font-bold uppercase text-[10px] tracking-widest">{a.severity}</span>{" "}
                    {a.item}{a.reaction ? ` — ${a.reaction}` : ""}
                  </li>
                ))}
              </ul>
            </div>
          )}
          {allergies.length > 0 && (
            <div className="flex flex-wrap gap-1" data-testid="mobile-ms-view-allergies">
              {allergies.map((a) => (
                <span
                  key={a}
                  className={`inline-flex items-center gap-1 px-1.5 py-0.5 text-[11px] rounded-full border ${ALLERGY_SEVERITY_STYLES[getAllergySeverity(a)]}`}
                >
                  <AlertTriangle className="w-3 h-3" /> {a}
                </span>
              ))}
            </div>
          )}
          {flags.length > 0 && (
            <div data-testid="mobile-ms-view-flags"><CareFlagsPills flags={flags} size="sm" /></div>
          )}

          {visit.notes && (
            <div className="rounded-md border border-slate-200 dark:border-slate-700 p-2">
              <div className="text-[9px] uppercase tracking-widest text-slate-500 font-semibold">Notes</div>
              <div className="text-xs text-slate-700 dark:text-slate-300 mt-0.5 whitespace-pre-wrap" data-testid="mobile-ms-view-notes">
                {visit.notes}
              </div>
            </div>
          )}

          {visit.published_at && (
            <div className="text-[11px] text-slate-500 dark:text-slate-400" data-testid="mobile-ms-view-published">
              Published {new Date(visit.published_at).toLocaleDateString()} · by {visit.published_by || "manager"}
            </div>
          )}
        </div>

        <DialogFooter className="gap-2 sm:gap-2 flex-col sm:flex-row">
          {/* Batch 4.3 · Lifecycle actions. Only the buttons appropriate
              for the current lifecycle state are rendered — this is the
              same gating desktop uses (`acceptance_status` / `status`
              /`check_in_at` checks) plus the `busy` in-flight guard to
              prevent double-taps that could produce duplicate writes. */}
          {canAct && state === "awaiting" && (
            <>
              <Button
                onClick={handleAccept}
                disabled={busy}
                data-testid={`mobile-ms-action-accept-${visit.id}`}
                className="h-11 w-full sm:w-auto bg-emerald-700 hover:bg-emerald-800 text-white"
              >
                <Check className="w-4 h-4 mr-1.5" aria-hidden /> Accept
              </Button>
              <Button
                variant="outline"
                onClick={handleDecline}
                disabled={busy}
                data-testid={`mobile-ms-action-decline-${visit.id}`}
                className="h-11 w-full sm:w-auto border-red-300 text-red-700 dark:text-red-400 dark:border-red-700"
              >
                <X className="w-4 h-4 mr-1.5" aria-hidden /> Decline
              </Button>
            </>
          )}
          {canAct && state === "accepted" && (
            <Button
              onClick={handleCheckIn}
              disabled={busy}
              data-testid={`mobile-ms-action-checkin-${visit.id}`}
              className="h-11 w-full sm:w-auto bg-sky-700 hover:bg-sky-800 text-white"
            >
              <LogIn className="w-4 h-4 mr-1.5" aria-hidden />
              {busy ? "Checking in…" : "Check in"}
            </Button>
          )}
          {canAct && state === "on_shift" && (
            <Button
              onClick={handleCheckOut}
              disabled={busy}
              data-testid={`mobile-ms-action-checkout-${visit.id}`}
              className="h-11 w-full sm:w-auto bg-slate-900 hover:bg-slate-800 text-white"
            >
              <LogOut className="w-4 h-4 mr-1.5" aria-hidden />
              {busy ? "Checking out…" : "Check out"}
            </Button>
          )}

          {visit.client_id && (
            <Button
              asChild
              variant="outline"
              className="h-11 w-full sm:w-auto"
              data-testid="mobile-ms-view-open-profile"
            >
              <Link to={`/clients/${visit.client_id}`} onClick={onClose}>
                <Clock className="w-4 h-4 mr-1.5" aria-hidden />
                Open profile
              </Link>
            </Button>
          )}
          <Button
            variant="outline"
            onClick={onClose}
            data-testid="mobile-ms-view-close"
            className="h-11 w-full sm:w-auto"
          >
            Close
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// -----------------------------------------------------------------------------
// Main list. Groups by day using the parent-supplied `byDay` map so the
// grouping logic is identical to the desktop view.
// -----------------------------------------------------------------------------
export default function MobileMyShiftsList({
  loading,
  shifts,
  byDay,
  dayKeys,
  siteById,
  safety,
  pendingCount,
  canAct = false,
  busyId = null,
  onAct,
  onCheck,
}) {
  const [openVisitId, setOpenVisitId] = useState(null);
  // Re-derive the open visit from the live `shifts` array on every render
  // so the dialog always reflects the latest server-returned attendance
  // fields ( check_in_at / check_out_at / status ) after `load()` runs.
  const openVisit = useMemo(
    () => (openVisitId ? shifts.find((v) => v.id === openVisitId) || null : null),
    [openVisitId, shifts],
  );

  const summary = useMemo(() => {
    const today = toLocalYMD(new Date());
    const weekEnd = new Date(); weekEnd.setDate(weekEnd.getDate() + 6);
    const weekEndIso = toLocalYMD(weekEnd);
    const todayCount = shifts.filter((v) => (v.scheduled_start || "").startsWith(today)).length;
    const weekCount = shifts.filter((v) => {
      const day = (v.scheduled_start || "").slice(0, 10);
      return day >= today && day <= weekEndIso;
    }).length;
    return { today: todayCount, week: weekCount, awaiting: pendingCount };
  }, [shifts, pendingCount]);

  return (
    <div
      className="lg:hidden flex-1 min-h-0 overflow-auto bg-slate-50 dark:bg-slate-950"
      data-testid="mobile-myshifts"
    >
      <div className="px-3 pt-3 pb-6 space-y-3 max-w-full">
        {/* Section title */}
        <div>
          <div className="text-[9px] uppercase tracking-widest text-slate-500 font-semibold inline-flex items-center gap-1">
            <CalendarCheck className="w-3 h-3 text-sky-700" /> Care delivery
          </div>
          <h2
            className="font-heading text-lg font-semibold text-slate-900 dark:text-slate-100"
            data-testid="mobile-myshifts-title"
          >
            My shifts
          </h2>
        </div>

        {/* Summary chips */}
        <div
          className="grid grid-cols-3 bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-lg overflow-hidden"
          data-testid="mobile-myshifts-summary"
        >
          <div className="p-3 text-center">
            <div className="text-[9px] uppercase tracking-widest text-slate-500 font-semibold">Today</div>
            <div className="text-lg font-heading font-semibold tabular-nums text-slate-900 dark:text-slate-100 mt-0.5" data-testid="mobile-myshifts-count-today">
              {summary.today}
            </div>
          </div>
          <div className="p-3 text-center border-x border-slate-100 dark:border-slate-800">
            <div className="text-[9px] uppercase tracking-widest text-slate-500 font-semibold">This week</div>
            <div className="text-lg font-heading font-semibold tabular-nums text-emerald-700 dark:text-emerald-400 mt-0.5" data-testid="mobile-myshifts-count-week">
              {summary.week}
            </div>
          </div>
          <div className="p-3 text-center">
            <div className="text-[9px] uppercase tracking-widest text-slate-500 font-semibold">Awaiting accept</div>
            <div className={`text-lg font-heading font-semibold tabular-nums mt-0.5 ${summary.awaiting > 0 ? "text-amber-700 dark:text-amber-400" : "text-slate-500"}`} data-testid="mobile-myshifts-count-awaiting">
              {summary.awaiting}
            </div>
          </div>
        </div>

        {/* Pending banner (informational, no CTA) */}
        {pendingCount > 0 && (
          <div
            className="rounded-lg border border-amber-300 bg-amber-50 dark:bg-amber-950/30 dark:border-amber-700 p-3 flex items-start gap-2"
            data-testid="mobile-myshifts-pending-banner"
          >
            <AlertTriangle className="w-4 h-4 text-amber-700 dark:text-amber-400 mt-0.5 shrink-0" />
            <div className="text-xs text-amber-900 dark:text-amber-200">
              <span className="font-semibold">{pendingCount} shift{pendingCount === 1 ? "" : "s"} awaiting your acceptance.</span>
            </div>
          </div>
        )}

        {/* Content */}
        {loading ? (
          <div className="text-sm text-slate-500 p-4" data-testid="mobile-myshifts-loading">Loading your shifts…</div>
        ) : shifts.length === 0 ? (
          <div
            className="rounded-lg border border-dashed border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-900 p-6 text-center"
            data-testid="mobile-myshifts-empty"
          >
            <CalendarCheck className="w-8 h-8 text-slate-300 dark:text-slate-600 mx-auto mb-2" />
            <div className="text-sm font-semibold text-slate-700 dark:text-slate-300">No upcoming shifts yet</div>
            <div className="text-xs text-slate-500 mt-1">
              When your manager publishes the rota you&apos;ll see everything here.
            </div>
          </div>
        ) : (
          <div className="space-y-4">
            {dayKeys.map((day) => (
              <section key={day} data-testid={`mobile-myshifts-day-${day}`}>
                <div className="flex items-center gap-2 mb-2 px-1">
                  <div className="text-[10px] uppercase tracking-widest text-slate-500 font-bold">
                    {dayHeaderLabel(day)}
                  </div>
                  <div className="text-[10px] text-slate-400 tabular-nums">
                    · {byDay[day].length} shift{byDay[day].length === 1 ? "" : "s"}
                  </div>
                </div>
                <div className="space-y-2">
                  {byDay[day].map((v) => (
                    <ShiftCard
                      key={v.id}
                      visit={v}
                      siteName={v.site_id ? siteById[v.site_id] : null}
                      safety={safety[v.client_id]}
                      onOpen={(vv) => setOpenVisitId(vv.id)}
                    />
                  ))}
                </div>
              </section>
            ))}
          </div>
        )}
      </div>

      {openVisit && (
        <ShiftDetailsDialog
          visit={openVisit}
          siteName={openVisit.site_id ? siteById[openVisit.site_id] : null}
          safety={safety[openVisit.client_id]}
          onClose={() => setOpenVisitId(null)}
          canAct={canAct}
          busyId={busyId}
          onAct={onAct}
          onCheck={onCheck}
        />
      )}
    </div>
  );
}
